// src/components/employees/SkillsManager.tsx
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Loader2 } from "lucide-react";
import { employeeService } from "@/services/employeeService";
import { useToast } from "@/hooks/use-toast";
import { Skill } from "@/types/employee";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
}

export function SkillsManager({ isOpen, onClose, slug }: Props) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [newSkill, setNewSkill] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadSkills = async () => {
    const res = await employeeService.getSkills(slug);
    setSkills(res.data.data || []);
  };

  useEffect(() => {
    if (isOpen) loadSkills();
  }, [isOpen]);

  const addSkill = async () => {
    if (!newSkill.trim()) return;
    try {
      setLoading(true);
      await employeeService.createSkill({ slug, name: newSkill });
      setNewSkill("");
      loadSkills();
      toast({ title: "Skill agregada" });
    } catch (e) {
      toast({ title: "Error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const deleteSkill = async (id: string) => {
    try {
      await employeeService.deleteSkill(id, slug);
      setSkills(skills.filter((s) => s.id !== id));
    } catch (e) {
      toast({ title: "Error al eliminar", variant: "destructive" });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Gestionar Habilidades</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 my-4">
          <Input
            placeholder="Nueva habilidad (ej. Soldadura)"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSkill()}
          />
          <Button onClick={addSkill} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-md min-h-[100px]">
          {skills.map((skill) => (
            <Badge
              key={skill.id}
              variant="outline"
              className="bg-white pl-2 pr-1 py-1 gap-1"
            >
              {skill.name}
              <button
                onClick={() => deleteSkill(skill.id)}
                className="hover:bg-red-100 rounded-full p-0.5 text-slate-400 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {skills.length === 0 && (
            <span className="text-slate-400 text-sm">
              No hay skills registradas
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
